import { Typography, Stack, Skeleton } from '@mui/material'
import { Box } from '@mui/system'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { APIError } from 'components/core/errors/api'
import { C1PagedList } from 'components/core/list/C1List/C1PagedList'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { TaskValue } from 'lib/actions/task'
import { SearchAppEntitlementUserBindingDestinationsRequest, SearchAppEntitlementUserBindingSourcesRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import React, { FC, useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchAppEntitlementUserBindingSourcesApi, searchAppEntitlementUserBindingDestinationsApi } from 'redux_toolkit/entitlements'

type HierarchyDrawerProps = {
    task: TaskValue
}

export const HierarchyDrawer: FC<HierarchyDrawerProps> = ({ task }) => {
    const [grantedFromPage, setGrantedFromPage] = useState<number>(0)
    const [grantedToPage, setGrantedToPage] = useState<number>(0)
    const pageSize = 10
    // Extract parameters from task
    const appId = task.app?.id
    const appEntitlementId = task.appEntitlements?.[0]?.id
    const appUserId = task.appUser?.id

    // Only make the API call if we have all required parameters
    const shouldFetch = Boolean(appId && appEntitlementId && appUserId)

    const {
        data: grantedToData,
        isLoading: grantedToIsLoading,
        error: grantedToError,
        getPage: getGrantedToPage,
        canLoadMore: grantedToCanLoadMore,
    } = searchAppEntitlementUserBindingDestinationsApi.useSearchAppEntitlementUserBindingDestinations(
        SearchAppEntitlementUserBindingDestinationsRequest.create(
            {
                appId: appId || '',
                appEntitlementId: appEntitlementId || '',
                appUserId: appUserId || '',
                pageToken: '',
            },
        ),
        {
            actionable: shouldFetch,
            pageSize,
        },
    )

    const {
        data: grantedFromData,
        isLoading: grantedFromIsLoading,
        error: grantedFromError,
        getPage: getGrantedFromPage,
        canLoadMore: grantedFromCanLoadMore,
    } = searchAppEntitlementUserBindingSourcesApi.useSearchAppEntitlementUserBindingSources(
        SearchAppEntitlementUserBindingSourcesRequest.create({
            appId,
            appEntitlementId,
            appUserId,
            pageToken: '',
        }),
        {
            actionable: shouldFetch,
            pageSize,
        },
    )

    const onGrantedFromPageChange = useCallback(
        async (n: number) => {
            await getGrantedFromPage(n)
            setGrantedFromPage(n)
        },
        [getGrantedFromPage],
    )

    const onGrantedToPageChange = useCallback(
        async (n: number) => {
            await getGrantedToPage(n)
            setGrantedToPage(n)
        },
        [getGrantedToPage],
    )

    const EntitlementListItem: FC<{ row: AppEntitlementRef }> = ({ row }) => (
        <Box
            sx={{
                border: 1,
                borderColor: 'border.emphasis',
                borderRadius: 1,
                p: 2,
                mb: 1,
                backgroundColor: 'background.paper',
                '&:hover': {
                    backgroundColor: 'grey.50',
                    borderColor: 'primary.main',
                },
                transition: 'all 0.2s ease-in-out',
            }}
        >
            <C1EntitlementDisplayNameWithFetch
                entitlementId={row.id}
                appId={row.appId}
            />
        </Box>
    )

    if (grantedFromIsLoading || grantedToIsLoading) {
        return <C1DrawerInner>
            <C1DrawerInner.Content>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: C1BorderRadius.large }} />
            </C1DrawerInner.Content>
        </C1DrawerInner>
    }

    if (grantedFromError || grantedToError) {
        return <C1DrawerInner>
            <C1DrawerInner.Content>
                <APIError err={grantedFromError} />
            </C1DrawerInner.Content>
        </C1DrawerInner>
    }

    return (
        <C1DrawerInner>
            <C1DrawerInner.Header>
                <Typography variant="h5">
                    <FormattedMessage
                        id="hierarchy_drawer_title"
                        defaultMessage="Entitlement relationships for {app_user} - {app_name} - {entitlement_name}"
                        values={{
                            app_user: task.appUser?.displayName || '',
                            app_name: task.app?.displayName || '',
                            entitlement_name: task.appEntitlements?.[0]?.displayName || '' }}
                    />
                </Typography>
            </C1DrawerInner.Header>
            <C1DrawerInner.Content>
                <Stack direction="column">
                    {grantedFromData && grantedFromData[0].length > 0 && (
                        <Stack direction="column" spacing={2}>
                            <Typography variant="h5">
                                <FormattedMessage
                                    id="hierarchy_drawer_title"
                                    defaultMessage="This entitlement is granted from:"
                                />
                            </Typography>
                            <C1PagedList<AppEntitlementRef>
                                data={grantedFromData || []}
                                ListItemComponent={EntitlementListItem}
                                isError={grantedFromError !== undefined}
                                isLoading={grantedFromIsLoading}
                                canLoadMore={grantedFromCanLoadMore}
                                pageSize={pageSize}
                                page={grantedFromPage}
                                onPageChange={onGrantedFromPageChange}
                            />
                        </Stack>
                    )}
                    {grantedToData && grantedToData[0].length > 0 && (
                        <Stack direction="column" spacing={2}>
                            <Typography variant="h5">
                                <FormattedMessage
                                    id="hierarchy_drawer_title"
                                    defaultMessage="This entitlement grants:"
                                />
                            </Typography>
                            <C1PagedList<AppEntitlementRef>
                                data={grantedToData || []}
                                ListItemComponent={EntitlementListItem}
                                isError={grantedToError !== undefined}
                                isLoading={grantedToIsLoading}
                                canLoadMore={grantedToCanLoadMore}
                                onPageChange={onGrantedToPageChange}
                                pageSize={pageSize}
                                page={grantedToPage}
                            />
                        </Stack>
                    )}
                    {
                        !grantedFromIsLoading && !grantedToIsLoading && !grantedFromError && !grantedToError &&
                        (!grantedFromData || grantedFromData[0]?.length === 0) &&
                        (!grantedToData || grantedToData[0]?.length === 0) && <Typography
                            variant="h5"
                            component="div"
                        >
                            <FormattedMessage
                                id="hierarchy_drawer_no_relationships"
                                defaultMessage="This entitlement has no hierarchical relationships."
                            />
                        </Typography>
                    }
                </Stack>
            </C1DrawerInner.Content>
        </C1DrawerInner>
    )
}
